import type Phaser from 'phaser';
import type { HeroId } from '../core';
import { PORTRAIT_FRAME, PORTRAIT_HOLE, TEX, heroPoseKey, portraitKey } from './assets';
import { bindTexture, type TextureWatch } from './bindTexture';
import type { HeroPose } from './cast';

/** Idle art is 512px tall. Every pose of a hero shares this scale so effects can stick out. */
export const HERO_PX = 512;

export interface HeroActor {
  sprite: Phaser.GameObjects.Image;
  setHero: (id: HeroId, pose: HeroPose) => void;
}

/**
 * Paint a gallery pose now, or when that painting finishes loading.
 * `installTextureBind` covers images created on other screens the same way.
 */
export function bindHeroTexture(
  scene: Parameters<typeof bindTexture>[0],
  sprite: Parameters<typeof bindTexture>[1],
  key: string,
  watch: TextureWatch,
): void {
  bindTexture(scene, sprite, key, watch);
}

function resetHero(
  scene: Phaser.Scene,
  sprite: Phaser.GameObjects.Image,
  x: number,
  feetY: number,
  scale: number,
): void {
  scene.tweens.killTweensOf(sprite);
  sprite.setPosition(x, feetY);
  sprite.setScale(scale);
  sprite.setAngle(0);
}

function playIdle(
  scene: Phaser.Scene,
  sprite: Phaser.GameObjects.Image,
  feetY: number,
  scale: number,
): void {
  scene.tweens.add({
    targets: sprite,
    y: feetY - 8,
    duration: 1700,
    yoyo: true,
    repeat: -1,
    ease: 'Sine.easeInOut',
  });
  scene.tweens.add({
    targets: sprite,
    scaleX: scale * 0.985,
    scaleY: scale * 1.035,
    duration: 1900,
    yoyo: true,
    repeat: -1,
    ease: 'Sine.easeInOut',
  });
}

/** Short squash and stretch. Each one finishes inside the HUD reaction window. */
function playReaction(
  scene: Phaser.Scene,
  sprite: Phaser.GameObjects.Image,
  pose: HeroPose,
  x: number,
  feetY: number,
  scale: number,
): void {
  if (pose === 'cheer') {
    scene.tweens.add({
      targets: sprite,
      y: feetY - 16,
      scaleX: scale * 0.92,
      scaleY: scale * 1.08,
      duration: 160,
      yoyo: true,
      ease: 'Sine.easeOut',
    });
    return;
  }
  if (pose === 'boast' || pose === 'smug') {
    scene.tweens.add({
      targets: sprite,
      scaleX: scale * 1.06,
      scaleY: scale * 1.06,
      duration: 180,
      yoyo: true,
      ease: 'Back.easeOut',
    });
    return;
  }
  if (pose === 'dismay') {
    scene.tweens.add({
      targets: sprite,
      y: feetY + 6,
      scaleX: scale * 1.06,
      scaleY: scale * 0.9,
      duration: 220,
      yoyo: true,
      ease: 'Sine.easeOut',
    });
    return;
  }
  if (pose === 'power') {
    scene.tweens.add({
      targets: sprite,
      scaleX: scale * 0.86,
      scaleY: scale * 1.12,
      duration: 140,
      yoyo: true,
      ease: 'Quad.easeOut',
    });
    return;
  }
  if (pose === 'hurt') {
    const dir = x < 360 ? -1 : 1;
    scene.tweens.add({
      targets: sprite,
      x: x + dir * 14,
      angle: dir * 8,
      scaleX: scale * 1.04,
      scaleY: scale * 0.92,
      duration: 90,
      yoyo: true,
      repeat: 1,
      ease: 'Quad.easeOut',
    });
  }
}

export function addHeroActor(
  scene: Phaser.Scene,
  x: number,
  feetY: number,
  id: HeroId,
  pose: HeroPose,
  scale: number,
  depth = 12,
): HeroActor {
  const sprite = scene.add
    .image(x, feetY, heroPoseKey(id, pose))
    .setOrigin(0.5, 1)
    .setScale(scale)
    .setDepth(depth);
  const watch: TextureWatch = { stop: null, armed: false };
  let current = id;
  let currentPose = pose;
  bindHeroTexture(scene, sprite, heroPoseKey(id, pose), watch);
  if (pose === 'idle') playIdle(scene, sprite, feetY, scale);
  else playReaction(scene, sprite, pose, x, feetY, scale);
  return {
    sprite,
    setHero(nextId, nextPose) {
      const poseChanged = nextPose !== currentPose;
      current = nextId;
      currentPose = nextPose;
      bindHeroTexture(scene, sprite, heroPoseKey(current, nextPose), watch);
      if (!poseChanged) return;
      resetHero(scene, sprite, x, feetY, scale);
      if (nextPose === 'idle') playIdle(scene, sprite, feetY, scale);
      else playReaction(scene, sprite, nextPose, x, feetY, scale);
    },
  };
}

export interface PortraitBits {
  face: Phaser.GameObjects.Image;
  frame: Phaser.GameObjects.Image;
  setHero: (id: HeroId) => void;
}

/** Round 256 portrait under the vine frame. `frameSize` is the frame's display size. */
export function addPortrait(
  scene: Phaser.Scene,
  x: number,
  y: number,
  id: HeroId,
  frameSize: number,
  depth = 22,
): PortraitBits {
  const hole = frameSize * (PORTRAIT_HOLE / PORTRAIT_FRAME);
  const face = scene.add.image(x, y, portraitKey(id)).setDisplaySize(hole, hole).setDepth(depth);
  const frame = scene.add
    .image(x, y, TEX.portraitFrame)
    .setDisplaySize(frameSize, frameSize)
    .setDepth(depth + 1);
  return {
    face,
    frame,
    setHero(next) {
      face.setTexture(portraitKey(next));
    },
  };
}
